import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { searchProducts } from '../../product/services/productService';

const LineItemsEditor = ({ lineItems, onLineItemsChange, vatPercentage = 21 }) => {
  const { t } = useTranslation();
  const debounceRef = useRef(null);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);

  const handleQuantityChange = (index, newQuantity) => {
    const quantity = parseFloat(newQuantity) || 0;
    if (quantity <= 0) {
      toast.warning(t('quantity_must_be_positive') || 'Quantity must be greater than 0');
      return;
    }

    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      Quantity: quantity,
    };
    onLineItemsChange(updatedItems);
  };

  // ✅ NEW: Handle unit price change
  const handleUnitPriceChange = (index, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    if (price < 0) {
      toast.warning(t('price_must_be_positive') || 'Price must be greater than or equal to 0');
      return;
    }

    const updatedItems = [...lineItems];
    const item = updatedItems[index];
    
    // Update unit prices (assume new price is incl VAT)
    const unitPriceInclVat = price;
    const unitPriceExclTax = price / (1 + vatPercentage / 100);
    
    updatedItems[index] = {
      ...item,
      UnitPriceInclVat: unitPriceInclVat,
      UnitPriceExclTax: unitPriceExclTax,
    };
    
    onLineItemsChange(updatedItems);
    toast.info(t('price_updated') || 'Price updated');
  };

  const handleRemoveItem = (index) => {
    // Prevent deletion of the last item
    if (lineItems.length === 1) {
      toast.warning(t('cannot_delete_last_item') || 'Cannot delete the last item. Delete the entire ticket instead.');
      return;
    }
    
    const updatedItems = lineItems.filter((_, i) => i !== index);
    onLineItemsChange(updatedItems);
    toast.info(t('item_removed') || 'Item removed');
  };

  const handleOpenAddProduct = () => {
    setAddProductDialogOpen(true);
    setSelectedProduct(null);
    setProductQuantity(1);
  };

  const handleCloseAddProduct = () => {
    setAddProductDialogOpen(false);
    setSelectedProduct(null);
    setProductQuantity(1);
  };

  // ✅ Product search with AsyncSelect (same as invoice page)
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        // ✅ Safely handle undefined or missing payload
        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        // ✅ Limit results to improve performance - only show first 50 products
        const limitedProducts = productsArray.slice(0, 50);

        callback(
          limitedProducts.map((p) => ({
            value: p.Id,
            label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? ' - SKU: ' + p.SKU : ''}${p.BarCode ? ' - BarCode: ' + p.BarCode : ''}`,
            ...p,
          }))
        );
      } catch (error) {

        toast.error(t('failed_to_search_products') || 'Failed to search products');
        callback([]);
      }
    }, 300);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.warning(t('please_select_product') || 'Please select a product');
      return;
    }

    const quantity = parseFloat(productQuantity) || 1;
    if (quantity <= 0) {
      toast.warning(t('quantity_must_be_positive') || 'Quantity must be greater than 0');
      return;
    }

    // Check if product already exists in line items
    const existingIndex = lineItems.findIndex(item => item.ProductId === selectedProduct.Id);
    
    if (existingIndex !== -1) {
      // Update existing item quantity
      const updatedItems = [...lineItems];
      updatedItems[existingIndex] = {
        ...updatedItems[existingIndex],
        Quantity: updatedItems[existingIndex].Quantity + quantity,
      };
      onLineItemsChange(updatedItems);
      toast.success(t('quantity_updated') || 'Quantity updated');
    } else {
      // Add new item
      const unitPriceExclTax = parseFloat(selectedProduct.SalePrice) / (1 + vatPercentage / 100);
      const unitPriceInclVat = parseFloat(selectedProduct.SalePrice);

      const newItem = {
        ProductId: selectedProduct.Id,
        ProductName: selectedProduct.Name,
        Quantity: quantity,
        UnitPriceExclTax: unitPriceExclTax,
        UnitPriceInclVat: unitPriceInclVat,
        Discount: 0,
        DiscountAmount: 0,
        VATAmount: (quantity * unitPriceExclTax * vatPercentage) / 100,
        Subtotal: quantity * unitPriceExclTax,
        TotalAmountInclVAT: quantity * unitPriceInclVat,
        TotalAmountInclVATAfterDiscount: quantity * unitPriceInclVat,
      };

      const updatedItems = [...lineItems, newItem];
      onLineItemsChange(updatedItems);
      toast.success(t('product_added') || 'Product added');
    }

    handleCloseAddProduct();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('line_items') || 'Line Items'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddProduct}
          size="small"
        >
          {t('add_product') || 'Add Product'}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('product') || 'Product'} / {t('service') || 'Service'}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 120 }}>
                {t('quantity') || 'Quantity'}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 140 }}>
                {t('unit_price') || 'Unit Price'} (€)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {t('total') || 'Total'}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>
                {t('action') || 'Action'}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('no_items_yet') || 'No items yet. Click "Add Product" to add items.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              lineItems.map((item, index) => (
                <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.ProductName || item.ServiceName || 'Item'}
                    </Typography>
                    {item.ServiceName && (
                      <Typography variant="caption" color="text.secondary">
                        {t('service') || 'Service'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={item.Quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      inputProps={{ min: 1, step: 1, style: { textAlign: 'center' } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={(item.UnitPriceInclVat || item.UnitPriceExclTax || 0).toFixed(2)}
                      onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'center' } }}
                      sx={{ width: 100 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(
                        item.Quantity * (item.UnitPriceInclVat || item.UnitPriceExclTax)
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('remove') || 'Remove'}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Product Dialog */}
      <Dialog
        open={addProductDialogOpen}
        onClose={handleCloseAddProduct}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('add_product') || 'Add Product'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Product Search with AsyncSelect */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {t('search_product') || 'Search Product'}
              </Typography>
              <AsyncSelect
                cacheOptions
                loadOptions={loadProductOptions}
                defaultOptions
                isClearable
                onChange={(selectedOption) => setSelectedProduct(selectedOption)}
                value={selectedProduct}
                placeholder={t('search_by_name_sku_barcode') || 'Search by name, SKU, or barcode...'}
                noOptionsMessage={() => t('no_products_found') || 'No products found'}
                loadingMessage={() => t('loading') || 'Loading...'}
                styles={{
                  container: (base) => ({ ...base, marginBottom: 8, width: '100%' }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Box>

            {/* Quantity Input */}
            {selectedProduct && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quantity') || 'Quantity'}
                  value={productQuantity}
                  onChange={(e) => setProductQuantity(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ mb: 2 }}
                />

                {/* Selected Product Info */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('selected_product') || 'Selected Product'}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {selectedProduct.Name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Price: €{parseFloat(selectedProduct.SalePrice || 0).toFixed(2)}
                    {selectedProduct.StockQuantity !== undefined && ` | Stock: ${selectedProduct.StockQuantity}`}
                  </Typography>
                  {selectedProduct.SKU && (
                    <Typography variant="caption" color="text.secondary">
                      SKU: {selectedProduct.SKU}
                    </Typography>
                  )}
                  {selectedProduct.BarCode && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      BarCode: {selectedProduct.BarCode}
                    </Typography>
                  )}
                </Paper>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddProduct} color="inherit">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            disabled={!selectedProduct}
          >
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LineItemsEditor;
